import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs, addDoc, doc, setDoc, runTransaction } from "firebase/firestore";
import { Save, ArrowLeft, User, MapPin, Plus, Trash2, X, Edit2, Phone, Mail, FileText, UserCheck } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import useClickOutside from './hooks/useClickOutside.js';
import useUnsavedChanges from './hooks/useUnsavedChanges.js';
import Spinner from './Spinner.js';
import { NewCustomerModal, AddAddressModal } from './CustomerModals.js';
import { useConfig } from './hooks/useConfig.js';
import { buildQuoteAuditFields } from './utils/quoteAudit.js';

// --- HELPER: Format Address Object to String ---
const formatAddress = (addr) => {
  if (!addr) return 'No Address Selected';
  return `${addr.address || ''}, ${addr.city || ''}, ${addr.state || ''} - ${addr.pincode || ''}`;
};

const getFirmPrefix = (firmId) => {
  if (firmId.startsWith('nexrise')) return 'NIIPL'; // Covers nexrise_mh, nexrise_rj, etc.
  if (firmId.startsWith('jagdish')) return 'JE';     // Covers jagdish_ent
  if (firmId.startsWith('cloudnine')) return 'C9';   // Covers cloudnine_mh, cloudnine_rj
  return 'GEN'; // Default fallback
};

const getDateAfterDays = (dateString, days = 30) => {
  if (!dateString) return '';
  const date = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
};

const normalizeLeadSource = (value) => {
  if (value === 'Word of Mouth') return 'Customer Referral';
  return value || '';
};

const leadSourceOptions = [
  'Indiamart',
  'Tradeindia',
  'Website',
  'Existing Customer',
  'Customer Referral',
  'Search Engine (Google, YouTube etc.)',
  'Social Media',
  'Other',
];

const CreateQuote = ({ db, user }) => {
  const { config } = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const customerSearchRef = useClickOutside(() => setShowCustSuggestions(false));

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  // --- 1. MAIN FORM STATE ---
  const today = new Date().toISOString().split('T')[0];
  const [formData, setFormData] = useState({
    firmId: '',
    date: today,
    expiryDate: getDateAfterDays(today, 30),
    quoteType: 'Sales',
    salesPerson: '',
    leadSource: '',
    subject: '',
    customerID: '',
    billingAddress: null,
    shippingAddress: null,
  });

  const [items, setItems] = useState([
    { id: 1, name: '', sku: '', description: '', hsn: '', qty: 1, rate: 0, discount: 0, tax: 18, total: 0 }
  ]);

  // --- 2. DATA LOAD STATE ---
  const [firms, setFirms] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [termsText, setTermsText] = useState(''); // New simple state for T&C text

  
  // --- 3. UI STATE ---
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [showCustSuggestions, setShowCustSuggestions] = useState(false);
  const [activeSearchRow, setActiveSearchRow] = useState(null); 
  const [filteredProducts, setFilteredProducts] = useState([]);
  
  // Toggle for Address "Edit Mode"
  // const [editBilling, setEditBilling] = useState(false);
  // const [editShipping, setEditShipping] = useState(false);

  // --- 4. MODAL STATES ---
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressModalType, setAddressModalType] = useState('billing'); 
  const [showModal, setShowModal] = useState(false); 
  const [customerDataToEdit, setCustomerDataToEdit] = useState(null); // State to hold customer data for editing
  const [addressDataToEdit, setAddressDataToEdit] = useState(null); // State to hold address data for editing
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const isInitialRender = useRef(true);

  const setSafeToNavigate = useUnsavedChanges(isDirty);

  // Track changes to form data
  useEffect(() => {
    if (loadingData) return;
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    setIsDirty(true);
  }, [formData, items, termsText, loadingData]);

  // --- INITIAL DATA LOADING ---
  useEffect(() => {
    const loadData = async () => {
      const firmSnap = await getDocs(collection(db, "firms"));
      const firmsList = firmSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setFirms(firmsList);

      const custSnap = await getDocs(collection(db, "customers"));
      const customersList = custSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllCustomers(customersList);

      const prodSnap = await getDocs(collection(db, "products"));
      setAllProducts(prodSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      if (location.state?.clonedQuote) {
        const { clonedQuote } = location.state;
        setFormData({
          firmId: clonedQuote.firmId,
          date: today, // Use today's date for clone
          expiryDate: getDateAfterDays(today, 30),
          quoteType: clonedQuote.quoteType,
          salesPerson: user?.displayName || user?.email || clonedQuote.salesPerson || '',
          leadSource: normalizeLeadSource(clonedQuote.leadSource),
          subject: clonedQuote.subject,
          customerID: clonedQuote.customerID,
          billingAddress: clonedQuote.billingAddress,
          shippingAddress: clonedQuote.shippingAddress,
        });
        setItems(clonedQuote.items || []);
        setTermsText(clonedQuote.terms ? clonedQuote.terms.join('\n') : '');
        const currentCustomer = customersList.find(c => c.id === clonedQuote.customerID);
        if (currentCustomer) {
          setCustomerSearchTerm(currentCustomer.display_name);
        }
      } else {
        if (firmsList.length > 0) {
          setFormData(prev => ({
            ...prev,
            firmId: firmsList[0].id,
            salesPerson: user?.displayName || user?.email || prev.salesPerson || '',
          }));
        }
      }
      setLoadingData(false);
    };
    loadData();
  }, [db, location.state, user, today]);

  // --- CUSTOMER SEARCH LOGIC ---
  const handleCustomerSearch = (value) => {
    setCustomerSearchTerm(value);
    if (value.length > 0) {
      const term = value.toLowerCase();
      const matches = allCustomers.filter(c => c.display_name?.toLowerCase().includes(term));
      setFilteredCustomers(matches);
      setShowCustSuggestions(true);
    } else {
      setFilteredCustomers(allCustomers);
      setShowCustSuggestions(true);
    }
  };

  const handleFirmChange = (e) => {
    const firmId = e.target.value;
    setFormData(p => ({ ...p, firmId }));

    // Load simple default terms into the textarea if available
    const selectedFirm = firms.find(f => f.id === firmId);
    if (selectedFirm?.terms) {
        // Convert the array of terms into a single string with new lines
        setTermsText(selectedFirm.terms.join('\n')); 
    } else {
        setTermsText("1. Payment terms: 100% advance.\n2. Delivery in 4-5 days.\n3. Taxes extra (@18%).");
    }
};

  const handleDateChange = (value) => {
    setFormData((prev) => {
      const previousDefaultExpiry = getDateAfterDays(prev.date, 30);
      const nextDefaultExpiry = getDateAfterDays(value, 30);
      const shouldSyncExpiry = !prev.expiryDate || prev.expiryDate === previousDefaultExpiry;

      return {
        ...prev,
        date: value,
        expiryDate: shouldSyncExpiry ? nextDefaultExpiry : prev.expiryDate,
      };
    });
  };

  const selectCustomer = (customer) => {
    setFormData(prev => ({ 
      ...prev, 
      customerID: customer.id,
      billingAddress: null,
      shippingAddress: null,
    }));
    setCustomerSearchTerm(customer.display_name);
    
    // Auto-select first available addresses
    const initialBilling = customer.addresses?.find(a => a.address_type === 'Billing');
    const initialShipping = customer.addresses?.find(a => a.address_type === 'Shipping');

    setFormData(prev => ({
        ...prev,
        billingAddress: initialBilling || null,
        shippingAddress: initialShipping || null,
    }));


    setShowCustSuggestions(false);
  };
  
  const selectedCustomer = allCustomers.find(c => c.id === formData.customerID);

  const handleAddressSelect = (type, value) => {
    if (value === 'new') {
      setAddressModalType(type);
      setShowAddressModal(true);
      return;
    }

    const address = selectedCustomer.addresses.find(a => a.id === value);
    if (type === 'billing') {

    } else {

    }
  };

  // --- SAVE QUOTE ---
  const getGrandTotal = () => items.reduce((sum, item) => {
    const base = (item.qty || 0) * (item.rate || 0);
    const val = base - (base * ((item.discount || 0) / 100));
    return sum + val + (val * ((item.tax || 0) / 100));
  }, 0);

  const calculateSubTotal = () => items.reduce((sum, item) => {
    const base = (item.qty || 0) * (item.rate || 0);
    return sum + (base - (base * ((item.discount || 0) / 100)));
  }, 0);

  const calculateGSTTotal = () => items.reduce((sum, item) => {
    const base = (item.qty || 0) * (item.rate || 0);
    const val = base - (base * ((item.discount || 0) / 100));
    return sum + (val * ((item.tax || 0) / 100));
  }, 0);

  const numberToWords = (num) => {
    const a = [
        '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
        'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'
    ];
    const b = [
        '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'
    ];
    const units = [
        { value: 10000000, str: 'Crore' },
        { value: 100000, str: 'Lakh' },
        { value: 1000, str: 'Thousand' },
        { value: 100, str: 'Hundred' }
    ];

    function convertNumberToWords(n) {
        let words = '';
        for (let i = 0; i < units.length; i++) {
            if (Math.floor(n / units[i].value) > 0) {
                words += convertNumberToWords(Math.floor(n / units[i].value)) + ' ' + units[i].str + ' ';
                n %= units[i].value;
            }
        }
        if (n > 19) {
            words += b[Math.floor(n / 10)] + ' ' + a[n % 10] + ' ';
        } 
        else if (n > 0) {
            words += a[n] + ' ';
        }
        return words.trim();
    }

    if (num === 0) return 'Zero Rupees Only';
    let rupees = Math.floor(num);
    let paisa = num % 1;
    let result = convertNumberToWords(rupees) + ' Rupees';
    if (paisa > 0) {
        paisa = paisa.toFixed(2).split('.')[1];
        result += ' and ' + convertNumberToWords(parseInt(paisa)) + ' Paise';
    }
    return result + ' Only';
  }

  const handleExportPdf = (quote) => {
    if (!quote || firms.length === 0) {
      alert('Data is not ready yet. Please wait a moment.');
      return;
    }

    const selectedFirm = firms.find(f => f.id === quote.firmId);
    if (!selectedFirm) {
        alert('Could not find firm details for this quote.');
        return;
    }

    let subTotal = 0;
    const itemsHtml = quote.items.map((item, index) => {
        const itemAmount = (item.qty || 0) * (item.rate || 0);
        const discountAmount = itemAmount * ((item.discount || 0) / 100);
        const netAmount = itemAmount - discountAmount;
        subTotal += netAmount;

        const descriptionWithLineBreaks = (item.description || '').replace(/\n/g, '<br />');
        const finalDescription = `<strong>${item.name}</strong><br />${descriptionWithLineBreaks}`;

        return `
            <tr class="item-row">
                <td>${index + 1}</td>
                <td>${item.sku || ''}</td>
                <td contenteditable="true">${finalDescription}</td>
                <td>${item.hsn || ''}</td>
                <td class="text-right">${item.qty || 0}</td>
                <td class="text-right">${(item.rate || 0).toFixed(2)}</td>
                <td class="text-right">${discountAmount.toFixed(2)}</td> 
                <td class="text-right">${netAmount.toFixed(2)}</td>
            </tr>
        `;
    }).join('');

    const gstAmount = subTotal * 0.18; // Note: This assumes a flat 18% GST on all items
    const totalAmount = subTotal + gstAmount;

    const formattedQuoteDate = new Date(quote.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const expiryDate = quote.expiryDate || getDateAfterDays(quote.date, 30);
    const formattedExpiryDate = expiryDate
      ? new Date(`${expiryDate}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      : 'N/A';

    const termsHtml = `<ol>${quote.terms.map(term => `<li>${term}</li>`).join('')}</ol>`;

    const embeddedCss = `
        .container { width: 1000px; margin: 0 auto; font-family: Arial, sans-serif; }
        table { width: 100%; border-collapse: collapse; }
        .header-table td { padding: 10px; vertical-align: middle; }
        .header-table{ border: solid 1px #1b1b1b; }
        .header-table p{ font-size:30px; font-weight: bold; }
        .logo { border-right:1px solid #1b1b1b; }
        .firm-details{ font-size:12px; }
        .firm-details strong{ font-size: 20px; }
        .Upper-table td { padding: 5px; text-align: left; width:25% }
        .Upper-table{ border-right: solid 1px #1b1b1b; border-left: solid 1px #1b1b1b; }
        .table-titles { background-color: #f2f2f2; border-bottom: 1px solid #1b1b1b; border-top: 1px solid #1b1b1b; font-weight: bold; }
        .item-table th, .item-table td { border: solid 1px #1b1b1b; padding: 5px; vertical-align: top; }
        .item-table th { background-color: #f9f9f9; text-align: left; }
        .text-right { text-align: right; }
        .tnc-table{ border-left: solid 1px #1b1b1b; border-right: solid 1px #1b1b1b; border-bottom: solid 1px #1b1b1b; }
        .terms-conditions { width: 70%; padding:10px; font-size: 12px; }
        .signature-area{ width: 30%; text-align:right; padding:10px; vertical-align: bottom; }
        table.Upper-table tr td:nth-child(2){border-right: solid 1px black}
        .doc-title p{margin: 5px}
    `;

    const quoteHtml = `
      <!DOCTYPE html><html><head><title>Quotation ${quote.quoteNumber}_${quote.customerName}</title><style>${embeddedCss}</style></head>
      <body>
          <div class="container">
            <table class="header-table">
                <tr>
                    <td class="logo" style="width: 20%;"><img width="150px" src="${selectedFirm.logo}" alt="${selectedFirm.name} Logo"></td>
                    <td class="firm-details">
                        <strong>${selectedFirm.name}</strong>,<br>
                        ${selectedFirm.address}<br>
                        GSTIN - ${selectedFirm.gstin}<br>
                        E-Mail - ${selectedFirm.email}<br>
                        Tel: ${selectedFirm.tel}
                    </td>
                    <td style="text-align: right; vertical-align: top;" class="doc-title"><p>QUOTE</p>${quote.quoteNumber}</td>
                </tr>
            </table>
            <table class="Upper-table">
              <tr>
                  <td><strong>Date:</strong></td><td>${formattedQuoteDate}</td>
                  <td><strong>Place of supply:</strong></td><td>${quote.shippingAddress?.state || ''}</td>
              </tr>
              <tr>
                  <td><strong>Expiry Date:</strong></td><td>${formattedExpiryDate}</td>
                  <td><strong>Salesperson</strong></td><td>${quote.salesPerson}</td>
              </tr>
              <tr class="table-titles">
                  <td colspan="2" style="border-right:1px solid #1b1b1b;"><strong>Billing Details:</strong></td>
                  <td colspan="2"><strong>Shipping Details:</strong></td>
              </tr>
              <tr>
                  <td><strong>Customer</strong></td><td>${quote.customerName}</td>
                  <td><strong>Ship to:</strong></td><td>${quote.shippingAddress?.attention || quote.customerName}</td>
              </tr>
              <tr>
                  <td><strong>Address</strong></td><td>${formatAddress(quote.billingAddress)}</td>   
                  <td><strong>Address</strong></td><td>${formatAddress(quote.shippingAddress)}</td>
              </tr>
              <tr>
                  <td><strong>Contact Person</strong></td><td>${quote.billingAddress?.attention || ''}</td>
                  <td><strong>Contact Person</strong></td><td>${quote.shippingAddress?.attention || ''}</td>
              </tr>
              <tr class="table-titles"><td colspan="4"><strong>Subject</strong></td></tr>
              <tr><td colspan="4">${quote.subject}</td></tr>
            </table>
            <table class="item-table">
                <thead>
                    <tr>
                        <th>Sr. No.</th><th>SKU</th><th style="width: 40%;">Item & Description</th><th>HSN/SAC</th><th class="text-right">Qty</th><th class="text-right">Rate</th><th class="text-right">Discount</th><th class="text-right">Amount</th>
                    </tr>
                </thead>
                <tbody>
                    ${itemsHtml}
                    <tr><td colspan="8" style="padding: 12px;"></td></tr>
                    <tr class="subtotal-row"><td colspan="5" rowspan="3">Total in Words: <br><strong>${numberToWords(Math.round(totalAmount))}</strong></td><td colspan="2">Sub Total</td><td class="text-right">Rs.${subTotal.toFixed(2)}</td></tr>
                    <tr class="gst-row"><td colspan="2">GST (18%)</td><td class="text-right">Rs.${gstAmount.toFixed(2)}</td></tr>
                    <tr class="total-row"><td colspan="2" style="font-weight:bold;">Total</td><td class="text-right" style="font-weight:bold;">Rs.${totalAmount.toFixed(2)}</td></tr>
                </tbody>
            </table>
            <table class="tnc-table">
                <tr>
                    <td class="terms-conditions"><h4>Terms & Conditions:</h4>${termsHtml}</td>
                    <td class="signature-area">
                        <img src="${selectedFirm.seal}" alt="Company Seal" style="max-width: 200px; max-height: 150px; opacity: 1;"><br>
                        <span><strong>For ${selectedFirm.name}<br>Authorized Signatory</strong></span>
                    </td>
                </tr>
            </table>
          </div>
      </body></html>
    `;

    const newWindow = window.open();
    newWindow.document.write(quoteHtml);
    newWindow.document.close();
    newWindow.focus();
    newWindow.addEventListener('afterprint', () => {
        newWindow.close();
    }, { once: true });
    newWindow.print();
  }

  // --- SAVE QUOTE (FINAL) ---
  const saveQuote = async (andExport = false) => {
    // --- VALIDATION START ---
    const requiredFields = {
      firmId: 'From (Firm)',
      date: 'Date',
      expiryDate: 'Expiry Date',
      salesPerson: 'Salesperson',
      quoteType: 'Quote Type', // Specific to quotes
      customerID: 'Customer',
      billingAddress: 'Billing Address',
      subject: 'Subject'
    };

    const missingFieldKeys = Object.keys(requiredFields).filter(field => {
      const value = formData[field];
      return !value || (typeof value === 'string' && value.trim() === '');
    });

    if (missingFieldKeys.length > 0) {
      const missingFieldNames = missingFieldKeys.map(key => requiredFields[key]).join(', ');
      alert(`Please fill out all required fields: ${missingFieldNames}.`);
      return;
    }

    if (items.length === 0 || (items.length === 1 && !items[0].name)) {
      alert('Please add at least one item to the quote.');
      return;
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (!item.name || (item.qty || 0) <= 0 || (item.rate || 0) <= 0) {
        alert(`Please provide a valid Name, positive Quantity, and positive Rate for item #${i + 1}.`);
        return;
      }
    }
    // --- VALIDATION END ---

    setIsSaving(true);
    const firmPrefix = getFirmPrefix(formData.firmId);
    const counterKey = firmPrefix.toLowerCase() + '_count';
    const sequencesRef = doc(db, "counters", "sequences");
    const yearCode = "25-26";

    try {
        const newCounter = await runTransaction(db, async (transaction) => {
            const sequenceDoc = await transaction.get(sequencesRef);
            if (!sequenceDoc.exists()) throw new Error("Counter document does not exist!");
            const currentCount = sequenceDoc.data()[counterKey] || 1000;
            const nextCount = currentCount + 1;
            transaction.update(sequencesRef, { [counterKey]: nextCount });
            return currentCount;
        });

        const quoteNumber = `${firmPrefix}/${yearCode}/QT${newCounter}`;
        const timestamp = new Date().toISOString();
        const quotePayload = {
            ...formData,
            quoteNumber: quoteNumber,
            customerName: selectedCustomer.display_name,
            billingAddress: formData.billingAddress,
            terms: termsText.split('\n').filter(t => t.trim() !== ''),
            shippingAddress: formData.shippingAddress || formData.billingAddress,
            items: items,
            grandTotal: getGrandTotal(),
            status: 'draft',
            createdAt: timestamp,
            createdBy: user?.displayName || user?.email || 'Unknown',
            createdByEmail: user?.email || '',
            createdByUid: user?.uid || '',
            ownerName: user?.displayName || user?.email || 'Unknown',
            ownerEmail: user?.email || '',
            ownerUid: user?.uid || '',
            ...buildQuoteAuditFields(user, timestamp)
        };

        const docRef = await addDoc(collection(db, "quotes"), quotePayload);
        alert(`Quote Saved Successfully! Number: ${quoteNumber}`);
        setIsDirty(false);
        setSafeToNavigate();

        const savedQuote = { ...quotePayload, id: docRef.id };

        if (andExport) {
            handleExportPdf(savedQuote);
        } else {
            navigate('/');
        }
    } catch (error) {
        setIsSaving(false);
        console.error("Error saving quote or transaction failed: ", error);
        alert("Error saving quote: " + error.message);
    }
};

  // --- ITEMS TABLE ---
  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    if (field === 'name') {
      setActiveSearchRow(index);
      if (value.length > 0) {
        const matches = allProducts.filter(p => p.name?.toLowerCase().includes(value.toLowerCase()) || p.sku?.toLowerCase().includes(value.toLowerCase()));
        setFilteredProducts(matches.slice(0, 10));
      } else setFilteredProducts([]);
    }
    setItems(newItems);
  };
  const selectProduct = (index, product) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], name: product.name, sku: product.sku, description: product.description || '', hsn: product.sac || '', rate: product.rate || 0, tax: product.tax || 18 };
    setItems(newItems);
    setActiveSearchRow(null);
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '10px' : '0 0 100px 0', paddingBottom: '100px' }}>
      
      {isSaving && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(255,255,255,0.7)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 9999
        }}>
          <div style={{ textAlign: 'center' }}>
            <Spinner />
            <div style={{ marginTop: '10px', fontWeight: 'bold', color: '#2563eb' }}>Saving Quote...</div>
          </div>
        </div>
      )}

      {/* HEADER */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', marginBottom: '20px', gap: isMobile ? '15px' : '0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Link to="/" style={{ color: '#6b7280' }}><ArrowLeft size={20} /></Link>
          <h2 style={{ margin: 0, fontSize: isMobile ? '20px' : '24px' }}>New {formData.quoteType} Quote</h2>
        </div>
        <div style={{ display: 'flex', gap: '10px', width: isMobile ? '100%' : 'auto' }}>
          <button onClick={() => saveQuote(false)} style={{ ...btnPrimaryStyle, flex: isMobile ? 1 : 'none', backgroundColor: 'white', color: 'black', border: '1px solid #d1d5db', justifyContent: 'center' }}>
            <Save size={18} color="black" /> {isMobile ? 'Save' : 'Save Quote'}
          </button>
          <button onClick={() => saveQuote(true)} style={{ ...btnPrimaryStyle, flex: isMobile ? 1 : 'none', backgroundColor: 'white', color: 'black', border: '1px solid #d1d5db', justifyContent: 'center' }}>
            <FileText size={18} color="black" /> {isMobile ? 'Export' : 'Save & Export PDF'}
          </button>
        </div>
      </div>

      {/* FORM GRID */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr', gap: '20px', marginBottom: '20px' }}>

        {/* LEFT: General Info */}
        <div style={{ ...cardStyle, padding: isMobile ? '15px' : '20px' }}>
           <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr', gap: '15px' }}>
              <div style={{ gridColumn: isMobile ? 'span 2' : 'auto' }}><label style={labelStyle}>From (Firm)</label><select name="firmId" value={formData.firmId} onChange={handleFirmChange} style={inputStyle}>{firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}</select></div>
              <div><label style={labelStyle}>Date</label><input type="date" name="date" value={formData.date} onChange={(e) => handleDateChange(e.target.value)} style={inputStyle} /></div>
              <div><label style={labelStyle}>Expiry Date</label><input type="date" name="expiryDate" value={formData.expiryDate || ''} min={formData.date || undefined} onChange={(e) => setFormData({...formData, expiryDate: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>Type</label><select name="quoteType" value={formData.quoteType} onChange={(e) => setFormData({...formData, quoteType: e.target.value})} style={inputStyle}><option>Sales</option><option>Rental</option></select></div>
              <div style={{ gridColumn: isMobile ? 'span 2' : 'auto' }}><label style={labelStyle}>Salesperson</label><select name="salesPerson" value={formData.salesPerson} disabled style={{ ...inputStyle, backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}><option value={formData.salesPerson || ''}>{formData.salesPerson || 'Auto-filled from profile'}</option></select></div>
              <div style={{ gridColumn: isMobile ? 'span 2' : 'auto' }}><label style={labelStyle}>Lead Source</label><select name="leadSource" value={formData.leadSource || ''} onChange={(e) => setFormData({...formData, leadSource: e.target.value})} style={inputStyle}><option value="">Select...</option>{leadSourceOptions.map(option => <option key={option} value={option}>{option}</option>)}</select></div>
           </div>
        </div>

        {/* RIGHT: Customer Details */}
       <div style={{ ...cardStyle, padding: isMobile ? '15px' : '20px' }}>
           {/* 1. SEARCH INPUT (Always Visible) */}
           <div style={{ marginBottom: selectedCustomer ? '20px' : '0' }}>
              <label style={labelStyle}><User size={12}/> Select Customer</label>
              <div style={{ position: 'relative' }} ref={customerSearchRef}>
                <input 
                  placeholder="Search by Display Name..." 
                  value={customerSearchTerm}
                  onFocus={() => { setFilteredCustomers(allCustomers); setShowCustSuggestions(true); }}
                  onChange={(e) => handleCustomerSearch(e.target.value)}
                  style={{ ...inputStyle, padding: '12px', fontSize: '15px' }}
                  autoComplete="off"
                />
                {showCustSuggestions && (
                  <div style={{ ...dropdownStyle, width: '100%' }}>
                    {filteredCustomers.map(cust => (
                      <div key={cust.id} onClick={() => selectCustomer(cust)} style={{...dropdownItemStyle, display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                        <span><strong>{cust.display_name}</strong> - {cust.company_name}</span>
                        {formData.customerID === cust.id && (
                          <button onClick={(e) => { e.stopPropagation(); setShowModal(true); setCustomerDataToEdit(cust); }} style={linkBtnStyle}>
                            <Edit2 size={12}/> Edit
                          </button>
                        )}
                      </div>
                    ))}
                    <div onClick={() => { setShowModal(true); setCustomerDataToEdit(null); setShowCustSuggestions(false); }} style={{ ...dropdownItemStyle, color: '#2563eb', fontWeight: 'bold', borderTop: '1px solid #e5e7eb', display:'flex', alignItems:'center', gap:5 }}>
                      <Plus size={16} /> Add New Customer
                    </div>
                  </div>
                )}
              </div>
           </div>

           {/* 2. CUSTOMER DETAILS (Visible after selection) */}
           {selectedCustomer && (
             <>
               <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5}}>
                 <h4 style={{margin:0, fontSize:'16px'}}>Customer: {selectedCustomer.display_name}</h4>
                 <button onClick={() => { setShowModal(true); setCustomerDataToEdit(selectedCustomer); }} style={linkBtnStyle}>
                   <Edit2 size={12}/> Edit Details
                 </button>
               </div>
               <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px', borderTop:'1px solid #e5e7eb', paddingTop:'20px', animation: 'fadeIn 0.3s' }}>
                  
                  {/* --- LEFT SIDE: BILLING --- */}
                  <div>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5}}>
                       <label style={{...labelStyle, marginBottom:0}}><MapPin size={12}/> Billing Address</label>
                       <div>
                         {formData.billingAddress && (
                           <button onClick={() => { setAddressDataToEdit(formData.billingAddress); setShowAddressModal(true); setAddressModalType('billing'); }} style={linkBtnStyle}><Edit2 size={12}/> Edit</button>
                         )}
                       </div>
                    </div>
                    
                    <select 
                      value={formData.billingAddress?.id || ''} 
                      onChange={(e) => handleAddressSelect('billing', e.target.value)}
                      style={{...inputStyle, borderColor: '#2563eb', backgroundColor:'#eff6ff'}}
                    >
                        <option value="">-- Select Address --</option>
                        {selectedCustomer?.addresses?.filter(a => a.address_type === 'Billing').map(addr => (
                              <option key={addr.id} value={addr.id}>{addr.attention || addr.city}</option>
                        ))}
                        <option value="new">+ Add New Address</option>
                    </select>
                    {formData.billingAddress && <div style={{...readOnlyBoxStyle, marginTop: '10px'}}>{formatAddress(formData.billingAddress)}</div>}
                    {/* PURCHASER CONTACT INFO */}
                        <div style={{marginTop: 15}}>
                            <label style={labelStyle}>Purchaser Details</label>
                            <div style={{fontSize:13, color:'#374151', display:'flex', flexDirection:'column', gap:5}}>
                              {formData.billingAddress?.attention && (
                                <div style={{display:'flex', gap:5, alignItems:'center', fontWeight:'bold'}}>
                                    <UserCheck size={12} color="#4b5563"/> {formData.billingAddress.attention}
                                </div>
                              )}
                              <div style={{display:'flex', gap:5, alignItems:'center'}}>
                                  <Phone size={12} color="#4b5563"/> {formData.billingAddress?.phone || selectedCustomer.mobile || '--'}
                              </div>
                              <div style={{display:'flex', gap:5, alignItems:'center'}}>
                                  <Mail size={12} color="#4b5563"/> {formData.billingAddress?.email || 'N/A'}
                              </div>
                            </div>
                        </div>
                  </div>

                  {/* --- RIGHT SIDE: SHIPPING --- */}
                  <div>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5}}>
                       <label style={{...labelStyle, marginBottom:0}}><MapPin size={12}/> Shipping Address</label>
                       <div>
                         {formData.shippingAddress && (
                           <button onClick={() => { setAddressDataToEdit(formData.shippingAddress); setShowAddressModal(true); setAddressModalType('shipping'); }} style={linkBtnStyle}><Edit2 size={12}/> Edit</button>
                         )}
                       </div>
                    </div>

                    <select 
                      value={formData.shippingAddress?.id || ''} 
                      onChange={(e) => handleAddressSelect('shipping', e.target.value)}
                      style={{...inputStyle, borderColor: '#2563eb', backgroundColor:'#eff6ff'}}
                    >
                        <option value="">-- Select Address --</option>
                        {selectedCustomer?.addresses?.filter(a => a.address_type === 'Shipping').map(addr => (
                              <option key={addr.id} value={addr.id}>{addr.attention || addr.city}</option>
                        ))}
                        <option value="new">+ Add New Address</option>
                    </select>
                    {formData.shippingAddress && <div style={{...readOnlyBoxStyle, marginTop: '10px'}}>{formatAddress(formData.shippingAddress)}</div>}

                    {/* SITE PERSON CONTACT INFO */}
                        <div style={{marginTop: 15}}>
                            <label style={labelStyle}>Site Person Details</label>
                            <div style={{fontSize:13, color:'#374151', display:'flex', flexDirection:'column', gap:5}}>
                              {(formData.shippingAddress?.attention || selectedCustomer.primary_contact?.first_name) && (
                                  <div style={{display:'flex', gap:5, alignItems:'center', fontWeight:'bold'}}>
                                      <UserCheck size={12} color="#4b5563"/> 
                                      {formData.shippingAddress?.attention || `${selectedCustomer.primary_contact?.first_name || ''} ${selectedCustomer.primary_contact?.last_name || ''}`}
                                  </div>
                              )}
                              <div style={{display:'flex', gap:5, alignItems:'center'}}>
                                  <Phone size={12} color="#4b5563"/> 
                                  {formData.shippingAddress?.phone || selectedCustomer.mobile || selectedCustomer.work_phone || '--'}
                              </div>
                              <div style={{display:'flex', gap:5, alignItems:'center'}}>
                                  <Mail size={12} color="#4b5563"/> 
                                  {formData.shippingAddress?.email || '--'}
                              </div>
                            </div>
                        </div>
                  </div>
               </div>

               {/* --- 3. SUBJECT SECTION (Full Width, Outside Grid) --- */}
               <div style={{ marginTop: '25px', borderTop:'1px solid #e5e7eb', paddingTop:'20px' }}>
                   <label style={{...labelStyle, fontSize:'12px'}}>Subject</label>
                   <input 
                      name="subject" 
                      value={formData.subject} 
                      onChange={(e) => setFormData(p => ({...p, subject: e.target.value}))} 
                      placeholder="Quote Subject (e.g. Quotation for 10M Aluminium Scaffold Tower)" 
                      style={{...inputStyle, padding:'12px', fontSize:'14px', width: '100%'}} 
                   />
               </div>
             </>
           )}
        </div>
      </div>

      {/* ITEMS TABLE */}
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '10px', 
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
        overflowX: isMobile ? 'auto' : 'visible',
        paddingBottom: activeSearchRow !== null ? '150px' : '0',
        transition: 'padding-bottom 0.2s'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '800px' : 'auto' }}>
          <thead style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
            <tr>
              <th style={thStyle}>#</th>
              <th style={{...thStyle, width: '40%'}}>Item Details</th>
              <th style={{...thStyle, width: '10%'}}>HSN/SAC</th>
              <th style={thStyle}>Qty</th>
              <th style={thStyle}>Rate</th>
              <th style={thStyle}>Discount (%)</th>
              <th style={thStyle}>Tax (%)</th>
              <th style={{...thStyle, textAlign: 'right'}}>Amount</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => {
              const rowAmount = (item.qty || 0) * (item.rate || 0) * (1 - (item.discount || 0) / 100) * (1 + (item.tax || 0) / 100);
              return (
              <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{...tdStyle, textAlign: 'center'}}>{index + 1}</td>
                <td style={{ ...tdStyle, position: 'relative' }}>
                  
                  {/* Name and SKU */}
                  <div style={{ position: 'relative', zIndex: activeSearchRow === index ? 100 : 1 }}>
                    <input
                      type="text"
                      value={item.name || ''}
                      onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                      placeholder="Search product by name..."
                      style={{ ...inputStyle, border: 'none', fontWeight: 'bold', padding: '10px 5px' }}
                    />
                    {item.sku && <div style={{ fontSize: '11px', color: '#6b7280', padding: '0 5px 5px' }}>SKU: {item.sku}</div>}

                    {/* Product Search Dropdown */}
                    {activeSearchRow === index && filteredProducts.length > 0 && (
                      <div style={{ ...dropdownStyle, width: '100%' }}>
                        {filteredProducts.map(prod => (
                          <div key={prod.id} onClick={() => selectProduct(index, prod)} style={dropdownItemStyle}>
                            <div style={{fontWeight: 'bold', fontSize:12}}>{prod.name} ({prod.sku})</div>
                            <div style={{fontSize:11, color:'#555'}}>{prod.description?.substring(0, 50)}...</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Editable Description */}
                  <textarea
                    value={item.description || ''}
                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                    placeholder="Additional description (editable)"
                    style={{ ...inputStyle, border: 'none', resize: 'vertical', marginTop: '5px', padding: '5px' }}
                    rows={2}
                  />

                </td>
                <td style={tdStyle}><input type="text" value={item.hsn} onChange={(e) => handleItemChange(index, 'hsn', e.target.value)} style={tableInputStyle} /></td>
                <td style={tdStyle}><input type="number" value={item.qty} onChange={(e) => handleItemChange(index, 'qty', parseFloat(e.target.value))} style={tableInputStyle} /></td>
                <td style={tdStyle}><input type="number" value={item.rate} onChange={(e) => handleItemChange(index, 'rate', parseFloat(e.target.value))} style={tableInputStyle} /></td>
                <td style={tdStyle}><input type="number" value={item.discount} onChange={(e) => handleItemChange(index, 'discount', parseFloat(e.target.value))} style={tableInputStyle} /></td>
                <td style={tdStyle}><input type="number" value={item.tax} onChange={(e) => handleItemChange(index, 'tax', parseFloat(e.target.value))} style={tableInputStyle} /></td>
                <td style={{...tdStyle, fontWeight:'bold', textAlign: 'right'}}>₹{rowAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td style={{...tdStyle, textAlign: 'center'}}><button onClick={() => setItems(items.filter((_, i) => i !== index))} style={{color:'#ef4444', border:'none', background:'none'}}><Trash2 size={16}/></button></td>
              </tr>
            )})
          }
          </tbody>
        </table>
        <div style={{ padding: '15px', display:'flex', justifyContent:'space-between' }}>
          <button onClick={() => setItems([...items, { id: Date.now(), name: '', sku:'', description:'', hsn: '', qty:1, rate:0, discount:0, tax:18, total:0 }])} style={{ color:'#2563eb', background:'none', border:'none', cursor:'pointer', fontWeight: 'bold' }}>+ Add Row</button>
        </div>
      </div>

      {/* --- SUMMARY SECTION --- */}
      <div style={{...cardStyle, marginTop: '20px', padding: isMobile ? '15px' : '20px' }}>
        <h3 style={{ margin: '0 0 15px 0', borderBottom: '1px solid #e5e7eb', paddingBottom: '10px', fontSize: isMobile ? '16px' : '18px' }}>Current Summary</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: isMobile ? '13px' : '15px' }}>
          <div style={{ fontWeight: 'bold' }}>Sub Total (after item discounts):</div>
          <div style={{ textAlign: 'right' }}>Rs.{calculateSubTotal().toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>

          <div style={{ fontWeight: 'bold' }}>GST (18%):</div>
          <div style={{ textAlign: 'right' }}>Rs.{calculateGSTTotal().toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>

          <div style={{ fontWeight: 'bold', fontSize: isMobile ? '16px' : '18px', borderTop: '1px solid #e5e7eb', paddingTop: '10px' }}>Grand Total:</div>
          <div style={{ textAlign: 'right', fontWeight: 'bold', fontSize: isMobile ? '16px' : '18px', borderTop: '1px solid #e5e7eb', paddingTop: '10px' }}>Rs.{getGrandTotal().toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
        </div>
      </div>
      
      {/* --- EDITABLE TERMS & CONDITIONS FOOTER SECTION --- */}
      <div style={{...cardStyle, marginBottom: '20px', marginTop: '20px', padding: isMobile ? '15px' : '20px'}}>
        <label style={labelStyle}>Terms & Conditions (Editable)</label>
        <textarea
            value={termsText}
            onChange={(e) => setTermsText(e.target.value)}
            style={{...inputStyle, width: '100%', minHeight: '120px', resize: 'vertical', fontSize: '13px'}}
            placeholder="Enter terms and conditions here (one per line). These will be saved with the quote."
        />
      </div>
      {/* --- END EDITABLE TERMS & CONDITIONS FOOTER SECTION --- */}

      {/* --- REUSABLE MODALS --- */}
      <NewCustomerModal 
        show={showModal} 
        onClose={() => { setShowModal(false); setCustomerDataToEdit(null); }} 
        onSave={(savedCust) => {
          setAllCustomers(prev => {
            const existingIndex = prev.findIndex(c => c.id === savedCust.id);
            if (existingIndex > -1) {
              return prev.map(c => c.id === savedCust.id ? savedCust : c);
            }
            return [...prev, savedCust];
          });
          selectCustomer(savedCust);
        }}
        db={db}
        customerData={customerDataToEdit}
        isMobile={isMobile}
      />

      <AddAddressModal
        show={showAddressModal}
        onClose={() => { setShowAddressModal(false); setAddressDataToEdit(null); }}
        onSave={(savedAddress, updatedAddresses) => {
          const updatedCustomer = { ...selectedCustomer, addresses: updatedAddresses };
          setAllCustomers(prev => prev.map(c => c.id === updatedCustomer.id ? updatedCustomer : c));
          if (savedAddress.address_type === 'Billing') { // Use savedAddress.address_type to ensure correct update
            setFormData(prev => ({ ...prev, billingAddress: savedAddress }));
            // setEditBilling(false); // Removed as editBilling state is removed
          } else {
            setFormData(prev => ({ ...prev, shippingAddress: savedAddress }));
            // setEditShipping(false); // Removed as editShipping state is removed
          }
        }}
        db={db}
        customer={selectedCustomer}
        addressType={addressModalType}
        addressData={addressDataToEdit} // Pass address data for editing
        isMobile={isMobile}
      />
    </div>
  );
};

// --- STYLES ---
const cardStyle = { backgroundColor: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const btnPrimaryStyle = { backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase' };
const inputStyle = { width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box' };
const dropdownStyle = { position: 'absolute', top: '100%', left: 0, width: '100%', maxHeight: '250px', overflowY: 'auto', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', zIndex: 100 };
const dropdownItemStyle = { padding: '10px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' };
const modalOverlayStyle = { position: 'fixed', top:0, left:0, right:0, bottom:0, backgroundColor: 'rgba(0,0,0,0.5)', display:'flex', justifyContent:'center', alignItems:'center', zIndex: 1000 };
const modalContentStyle = { backgroundColor: 'white', width: '900px', maxWidth:'95%', borderRadius: '8px', padding: '20px', display:'flex', flexDirection:'column', position: 'relative' };
const rowStyle = { display: 'flex', gap: '15px', marginBottom: '15px' };
const sectionHeaderStyle = { margin: '20px 0 10px 0', borderBottom:'1px solid #eee', paddingBottom:5, color:'#374151', fontSize: '14px', fontWeight:'bold' };
const readOnlyBoxStyle = { background: '#f8fafc', padding: '10px', borderRadius: '5px', border: '1px solid #e5e7eb', fontSize: '13px', color: '#374151', minHeight: '40px' };
const linkBtnStyle = { background:'none', border:'none', color:'#2563eb', cursor:'pointer', fontSize:'12px', display:'flex', alignItems:'center', gap:4, padding:0 };
const tableInputStyle = { width: '100%', padding: '5px', border: '1px solid transparent', backgroundColor: 'transparent', textAlign: 'center', fontSize: '13px' };
const thStyle = { padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' };
const tdStyle = { padding: '10px', verticalAlign: 'top', fontSize: '13px' };

export default CreateQuote;
